import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { chatThreadRepo, chatMessageRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Avatar, Badge, EmptyState, NotConnected, PageHead, formatTime, useAsync } from "../../components/ui";

const CHANNEL_ICON = { portal: "💬", line: "🟢", facebook: "🔵", instagram: "🟣" };
const CHANNEL_NAME = { portal: "พอร์ทัลลูกค้า", line: "LINE", facebook: "Facebook", instagram: "Instagram" };

export default function Chat() {
  const { orgId } = useApp();
  const { tx } = useI18n();
  const [params] = useSearchParams();
  const { data: threads, loading, reload } = useAsync(() => chatThreadRepo.all(orgId).then((rows) => [...rows].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))), [orgId], []);
  const [activeId, setActiveId] = useState(params.get("thread"));
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (threads?.length && !activeId) setActiveId(threads[0].id);
  }, [threads, activeId]);

  useEffect(() => {
    if (!activeId) return;
    chatMessageRepo.all(orgId, (row) => row.threadId === activeId).then((rows) =>
      setMessages(rows.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)))
    );
  }, [orgId, activeId]);

  const active = threads?.find((t) => t.id === activeId);
  // ส่งได้เฉพาะแชทในระบบ (พอร์ทัลลูกค้า) — LINE/FB/IG ยังไม่เชื่อมต่อ ห้ามแกล้งว่าส่งออกไป
  const canSend = active?.channel === "portal";

  // เปิดอ่านแล้วเคลียร์จำนวนที่ยังไม่อ่าน
  useEffect(() => {
    if (active?.unread) chatThreadRepo.update(orgId, active.id, { unread: 0 }).then(reload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.unread]);

  const send = async () => {
    if (!draft.trim() || !activeId || !canSend) return;
    const now = new Date().toISOString();
    const text = draft.trim();
    const message = await chatMessageRepo.create(orgId, { threadId: activeId, from: "staff", text, createdAt: now });
    await chatThreadRepo.update(orgId, activeId, { lastMessage: text, updatedAt: now });
    setMessages((rows) => [...rows, message]);
    setDraft("");
    reload();
  };

  return (
    <>
      <PageHead title={tx("กล่องแชทรวม", "Omnichannel chat")} subtitle={tx("แชทกับลูกค้าผ่านพอร์ทัลของร้าน · LINE / Facebook / Instagram ยังไม่เชื่อมต่อ", "Chat with customers via the shop portal · LINE / Facebook / Instagram not connected")} />

      <div className="chat-layout">
        <div className="chat-list">
          {loading ? null : threads?.length === 0 ? (
            <EmptyState text={tx("ยังไม่มีบทสนทนา", "No conversations")} />
          ) : (
            threads.map((thread) => (
              <div key={thread.id} className={`chat-item ${activeId === thread.id ? "is-active" : ""}`} onClick={() => setActiveId(thread.id)}>
                <Avatar name={thread.customerName} />
                <div className="chat-item__body">
                  <div className="chat-item__name">
                    {thread.customerName} <span>{CHANNEL_ICON[thread.channel]}</span>
                    {thread.unread ? <Badge tone="red">{thread.unread}</Badge> : null}
                  </div>
                  <div className="chat-item__msg">{thread.lastMessage}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="chat-panel">
          {active ? (
            <>
              <div className="chat-panel__head">
                <Avatar name={active.customerName} />
                <div>
                  <div className="u-strong">{active.customerName}</div>
                  <div className="u-fs-12 u-muted">{CHANNEL_ICON[active.channel]} {CHANNEL_NAME[active.channel] || active.channel}</div>
                </div>
              </div>
              <div className="chat-panel__body">
                {messages.map((message) => (
                  <div key={message.id} className={`chat-bubble ${message.from === "staff" ? "is-mine" : ""}`} style={message.from === "staff" ? { marginLeft: "auto" } : undefined}>
                    {message.text}
                    <span className="chat-bubble__time">{formatTime(message.createdAt)}</span>
                  </div>
                ))}
              </div>
              {canSend ? (
                <div className="chat-panel__foot">
                  <input
                    className="input-crm"
                    placeholder={tx("พิมพ์ข้อความ...", "Type a message...")}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => event.key === "Enter" && send()}
                  />
                  <button type="button" className="btn-crm btn-primary-crm" onClick={send}>{tx("ส่ง", "Send")}</button>
                </div>
              ) : (
                <div className="chat-panel__foot">
                  <NotConnected label={CHANNEL_NAME[active.channel] || active.channel} hint={tx("ตอบกลับผ่านช่องทางนี้ไม่ได้", "Cannot reply via this channel")} />
                  <span className="u-fs-12 u-muted">{tx("ยังส่งข้อความออกไปช่องทางนี้ไม่ได้ จึงปิดช่องพิมพ์ไว้", "Replies cannot be sent to this channel yet")}</span>
                </div>
              )}
            </>
          ) : (
            <EmptyState text={tx("เลือกบทสนทนาทางซ้าย", "Select a conversation")} />
          )}
        </div>
      </div>
    </>
  );
}
